// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — lone data texels on a globe drawn smaller than its stream
 * (owner, 2026-10-10: "Marine Vessel Traffic looks great from R2 but it
 * looks like missing most of the points in XR").
 *
 * A value-encoded texture is sampled NEAREST: its luma is a code, and
 * blending two codes would make a third that means something else. So
 * each screen pixel reads exactly one texel. When the globe is drawn
 * smaller than the stream (a 1800-texel-wide world on a globe a few
 * hundred pixels across, or anywhere near its limb), several texels fall
 * under one pixel and only one of them is ever read. A sparse field —
 * vessel presence, fire detections — is mostly "no data" with lone data
 * texels, and most of those are simply never picked: about 16 % survive
 * at four texels per pixel (measured on the gfw_traffic stream).
 *
 * The fix, `sparsePick`: when the texel under the pixel's centre is no
 * data and the pixel spans more than one texel, look at up to 4×4 texels
 * across its footprint and take the highest code, which on these streams
 * is also the largest value (the busiest cell). A texel with data under
 * the centre is always kept as it is, so dense fields draw exactly as
 * before; only pixels that would have been empty change, and only to a
 * code that really is inside them. Datasets without a transparent band
 * (cutoff 0) never search.
 *
 * The footprint comes from the derivatives of the mesh's own UV, taken
 * before any branch (derivatives inside non-uniform control flow are
 * undefined) and scaled by how many map units one UV unit spans (1 on the
 * full globe; 360 / box width and 180 / box height for a regional box).
 */

/** The most texels per axis one pixel looks at: 16 reads at worst, only on empty pixels. */
export const SPARSE_PICK_MAX_TAPS = 4

/**
 * GLSL ES 3.00 (three.js compiles every material as WebGL2 GLSL 3):
 *
 *  - `sparseFootprint(uv)`: how far one screen pixel reaches in `uv`, per axis.
 *    Call it at the top of the shader, before any branch or discard.
 *  - `sparsePick(tex, region, local, localFw, cutoff)`: the map coordinate
 *    (0..1 inside `region`, the map's rectangle of the frame as
 *    `(u0, v0, uScale, vScale)`) whose texel this pixel should be coloured
 *    by. `localFw` is the footprint in those map coordinates and `cutoff`
 *    the first visible code (0: never search).
 */
export const SPARSE_PICK_GLSL = /* glsl */ `
  vec2 sparseFootprint(vec2 uv) {
    return max(abs(dFdx(uv)), abs(dFdy(uv)));
  }

  vec2 sparsePick(sampler2D tex, vec4 region, vec2 local, vec2 localFw, float cutoff) {
    if (cutoff <= 0.0) return local;
    float code = floor(textureLod(tex, region.xy + local * region.zw, 0.0).r * 255.0 + 0.5);
    if (code >= cutoff) return local;
    vec2 size = vec2(textureSize(tex, 0)) * abs(region.zw);
    vec2 span = localFw * size;
    if (max(span.x, span.y) <= 1.0) return local;
    vec2 n = clamp(ceil(span), vec2(1.0), vec2(${SPARSE_PICK_MAX_TAPS}.0));
    // Half a texel inside the map, so a read never lands on the frame
    // around it (a calibration strip, another rectangle's class map).
    vec2 lo = 0.5 / size;
    vec2 best = local;
    float bestCode = code;
    for (int j = 0; j < ${SPARSE_PICK_MAX_TAPS}; j++) {
      if (float(j) >= n.y) break;
      for (int i = 0; i < ${SPARSE_PICK_MAX_TAPS}; i++) {
        if (float(i) >= n.x) break;
        vec2 at = clamp(local + ((vec2(float(i), float(j)) + 0.5) / n - 0.5) * localFw, lo, 1.0 - lo);
        float c = floor(textureLod(tex, region.xy + at * region.zw, 0.0).r * 255.0 + 0.5);
        if (c > bestCode) {
          bestCode = c;
          best = at;
        }
      }
    }
    return best;
  }
`

/**
 * The `sparsePick` cutoff for a palette table (256 RGBA entries per row,
 * indexed by code; row 0 is the default palette and carries the alpha every
 * row shares): the first code it draws with any opacity. 0 — never look
 * around — when code 0 is drawn (a field with no transparent band), when
 * nothing is, or without a table. Read off the table the shader colours
 * with, so it follows Tools → View too.
 */
export function firstDrawnCode(lut: Uint8Array | null | undefined): number {
  if (!lut || lut.length < 1024) return 0
  for (let code = 0; code < 256; code++) {
    if (lut[code * 4 + 3]! > 0) return code
  }
  return 0
}
